import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  formatPermissionGroupLabel,
  getFamilyPermissionIds,
  getModulePermissionIds,
  groupPermissionsByFamily,
} from '../../utils/permissionGrouping';

export default function RolePermissionsPicker({
  allPermissions = [],
  selectedPermissionIds = [],
  onSelectedPermissionIdsChange,
  searchText = '',
  onSearchTextChange,
  loading = false,
  maxHeight = 420,
}) {
  const filteredPermissions = useMemo(
    () =>
      allPermissions.filter((permission) =>
        String(permission.name || '')
          .toLowerCase()
          .includes(String(searchText || '').toLowerCase())
      ),
    [allPermissions, searchText]
  );

  const permissionFamilies = useMemo(
    () => groupPermissionsByFamily(filteredPermissions),
    [filteredPermissions]
  );

  const familyKeys = useMemo(
    () =>
      Object.keys(permissionFamilies).sort((a, b) =>
        formatPermissionGroupLabel(a).localeCompare(formatPermissionGroupLabel(b))
      ),
    [permissionFamilies]
  );

  const filteredPermissionIds = filteredPermissions.map((p) => p.id);

  const isIdsFullySelected = (ids) =>
    ids.length > 0 && ids.every((id) => selectedPermissionIds.includes(id));

  const isIdsPartiallySelected = (ids) => {
    const selectedCount = ids.filter((id) => selectedPermissionIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < ids.length;
  };

  const toggleIdsSelection = (ids, checked) => {
    if (checked) {
      onSelectedPermissionIdsChange([...new Set([...selectedPermissionIds, ...ids])]);
    } else {
      onSelectedPermissionIdsChange(selectedPermissionIds.filter((id) => !ids.includes(id)));
    }
  };

  const handlePermissionChange = (permissionId) => (event) => {
    if (event.target.checked) {
      onSelectedPermissionIdsChange([...selectedPermissionIds, permissionId]);
    } else {
      onSelectedPermissionIdsChange(selectedPermissionIds.filter((id) => id !== permissionId));
    }
  };

  const isAllFilteredSelected =
    filteredPermissionIds.length > 0 &&
    filteredPermissionIds.every((id) => selectedPermissionIds.includes(id));

  const isSomeFilteredSelected =
    filteredPermissionIds.some((id) => selectedPermissionIds.includes(id)) && !isAllFilteredSelected;

  const handleSelectAllFiltered = (event) => {
    toggleIdsSelection(filteredPermissionIds, event.target.checked);
  };

  const isModuleFullySelected = (module, modulesMap) =>
    isIdsFullySelected(getModulePermissionIds(module, modulesMap));

  const isModulePartiallySelected = (module, modulesMap) =>
    isIdsPartiallySelected(getModulePermissionIds(module, modulesMap));

  const handleModuleCheckAll = (module, modulesMap) => (event) => {
    toggleIdsSelection(getModulePermissionIds(module, modulesMap), event.target.checked);
  };

  const isFamilyFullySelected = (family) =>
    isIdsFullySelected(getFamilyPermissionIds(family, permissionFamilies));

  const isFamilyPartiallySelected = (family) =>
    isIdsPartiallySelected(getFamilyPermissionIds(family, permissionFamilies));

  const handleFamilyCheckAll = (family) => (event) => {
    toggleIdsSelection(getFamilyPermissionIds(family, permissionFamilies), event.target.checked);
  };

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search permissions..."
        value={searchText}
        onChange={(e) => onSearchTextChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ mb: 2 }} />

      {!loading && filteredPermissions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllFilteredSelected}
                indeterminate={isSomeFilteredSelected}
                onChange={handleSelectAllFiltered}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight="medium">
                Check all (visible permissions)
              </Typography>
            }
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ maxHeight, overflow: 'auto' }}>
          {familyKeys.length > 0 ? (
            familyKeys.map((family) => {
              const modulesMap = permissionFamilies[family];
              const moduleKeys = Object.keys(modulesMap).sort((a, b) =>
                formatPermissionGroupLabel(a).localeCompare(formatPermissionGroupLabel(b))
              );
              const familyCount = getFamilyPermissionIds(family, permissionFamilies).length;
              const showFamilyCheckAll = moduleKeys.length > 1 || familyCount > 1;

              return (
                <Box
                  key={family}
                  sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                      {formatPermissionGroupLabel(family)} module
                    </Typography>
                    <Chip label={familyCount} size="small" color="primary" variant="outlined" />
                    {showFamilyCheckAll && (
                      <FormControlLabel
                        sx={{ ml: 'auto' }}
                        control={
                          <Checkbox
                            checked={isFamilyFullySelected(family)}
                            indeterminate={isFamilyPartiallySelected(family)}
                            onChange={handleFamilyCheckAll(family)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight="medium">
                            Check all {formatPermissionGroupLabel(family)} related
                          </Typography>
                        }
                      />
                    )}
                  </Box>

                  {moduleKeys.map((module) => (
                    <Accordion
                      key={`${family}-${module}`}
                      defaultExpanded
                      sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategoryIcon color="action" fontSize="small" />
                          <Typography variant="body2" fontWeight="medium">
                            {formatPermissionGroupLabel(module)}
                          </Typography>
                          <Chip label={modulesMap[module].length} size="small" variant="outlined" />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <FormControlLabel
                          sx={{ mb: 1, ml: 0.5 }}
                          control={
                            <Checkbox
                              checked={isModuleFullySelected(module, modulesMap)}
                              indeterminate={isModulePartiallySelected(module, modulesMap)}
                              onChange={handleModuleCheckAll(module, modulesMap)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight="medium">
                              Check all
                            </Typography>
                          }
                        />
                        <Grid container spacing={1}>
                          {modulesMap[module].map((permission) => (
                            <Grid item xs={12} sm={6} key={permission.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedPermissionIds.includes(permission.id)}
                                    onChange={handlePermissionChange(permission.id)}
                                    color="primary"
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontFamily="monospace">
                                    {permission.name}
                                  </Typography>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              );
            })
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {allPermissions.length === 0
                  ? 'No permissions available'
                  : 'No permissions found matching your search'}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CheckCircleIcon fontSize="small" />
        <Typography variant="body2">
          Selected {selectedPermissionIds.length} permission(s)
        </Typography>
      </Paper>
    </Box>
  );
}
